import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useContext, useState } from "react";
import { UserContext } from "./$userId";
import {
  createOrCompleteAccessCardOrder,
  useCompany,
  useUser,
  useBuilding,
  userOptions,
  companyOptions,
  useSubdivisionById,
} from "@/api";
import { CardType, NotificationType, CompanyType } from "@/types";
import {
  useLoggedInUser,
  useRouterStore,
  useSnackbarStore,
} from "@/store/zustandStore";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getBuildingId, notifyApiError } from "@/lib";
import { useCompanyAdminAccess } from "@/hooks/useCompanyAdminAccess";
import { useWelcomeAccessAccess } from "@/hooks/useWelcomeAccessAccess";
import { useAccessAdminPermission } from "@/hooks/useAccessAdminPermission";
import AccessCardsAdminLevelWithIntegration from "@/components/access_cards/AccessCardsAdminLevelWithIntegration";
import { AcsConnectivityBanner } from "@/components/AcsConnectivityBanner";
import { Button, InputField, PopupCard } from "@app-components";
import styles from "@/styles/Access.module.scss";

export const Route = createFileRoute(
  "/_portal/company/$companyId/users/$userId/access",
)({
  component: UserDetailsAccess,
  loader: ({ params }) => {
    const { queryClient } = useRouterStore.getState();
    if (!queryClient) return;
    queryClient.ensureQueryData(userOptions(params.userId, params.companyId));
    queryClient.ensureQueryData(companyOptions(params.companyId));
  },
});

function UserDetailsAccess() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const currentUser = useLoggedInUser();
  const { companyId } = Route.useParams();
  const { userId, userDisabled } = useContext(UserContext);
  const { addNotification } = useSnackbarStore();
  const { data: user } = useUser(userId, companyId);
  const { data: company } = useCompany(user.buildingTenantId);
  const { data: subdivision } = useSubdivisionById(
    user.subdivisionId,
    user.buildingTenantId,
  );
  const { data: building } = useBuilding();
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderNote, setOrderNote] = useState("");
  const [isOrdering, setIsOrdering] = useState(false);
  const { allowCompanyAdminAccess } = useCompanyAdminAccess(
    companyId,
    companyId,
  );
  const navigate = useNavigate();
  const { isCompanyAccessAdmin } = useAccessAdminPermission();
  const canManageCompanyAccess = isCompanyAccessAdmin(companyId);

  const { companyAccessControlSystemIntegrated } =
    useWelcomeAccessAccess(false);

  // Add access tab route guard
  const showAccessTab =
    building.accessControlSystemIntegration &&
    company.accessControlSystemIntegration;
  const isExternalCompany = company?.companyType === CompanyType.EXTERNAL;
  if (!showAccessTab || isExternalCompany) {
    navigate({ to: "/401", replace: true });
    return null;
  }

  const handleCreatePlasticCardOrder = async () => {
    if (userDisabled) return;
    // TODO: add validitiy check on orderNote
    try {
      setIsOrdering(true);
      const res = await createOrCompleteAccessCardOrder(
        CardType.PHYSICAL,
        {
          userId: currentUser.userId,
          company: company.buildingTenantName,
          email: currentUser.email,
        },
        userId,
        companyId,
        orderNote,
      );
      setShowOrderModal(false);
      addNotification(
        t(`NOTIFICATIONS.${res.data.code}`, {
          entity: t("ACCESS_CARD.CARD_ORDER").toLowerCase(),
        }),
        NotificationType.SUCCESS,
      );
      queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "access-cards"],
      });
      queryClient.invalidateQueries({
        queryKey: [
          getBuildingId(),
          "ordered-access-cards",
          userId,
          companyAccessControlSystemIntegrated,
        ],
      });
    } catch (error) {
      console.error(error);
      notifyApiError(error, addNotification, t, {
        properties: {
          entity: t("ACCESS_CARD.CARD_ORDER").toLowerCase(),
        },
        fallbackKey: "CREATE_FAILED",
      });
    } finally {
      setIsOrdering(false);
    }
  };

  if (!allowCompanyAdminAccess) {
    return null;
  }
  return (
    <>
      <AcsConnectivityBanner />
      {companyAccessControlSystemIntegrated ? (
        <AccessCardsAdminLevelWithIntegration
          disabled={userDisabled}
          user={user}
          company={company}
          isOrdering={isOrdering}
          canManageAccess={canManageCompanyAccess}
          setShowOrderModal={setShowOrderModal}
        />
      ) : null}
      <PopupCard
        onClose={() => setShowOrderModal(false)}
        open={showOrderModal && !userDisabled}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--wlcm-spacing-sm)",
          }}
        >
          <h3>{t("ACCESS_CARD.ORDER_PHYSICAL")}</h3>
          <div>
            <p className={styles.startAligned}>
              <strong>{t("ACCESS_CARD.TABLE_HEADERS.RECIPIENT")}:</strong>{" "}
              <span>
                {user.firstName} {user.lastName} ({user.email})
              </span>
            </p>
            <p className={styles.startAligned}>
              <strong>{t("ACCESS_CARD.TABLE_HEADERS.COMPANY")}:</strong>{" "}
              <span>{company.buildingTenantName}</span>
            </p>
            {subdivision?.name && (
              <p className={styles.startAligned}>
                <strong>{t("SUBDIVISIONS.SUBDIVISION")}:</strong>{" "}
                <span>{subdivision.name}</span>
              </p>
            )}
          </div>
          <div>
            <label htmlFor="ORDER_NOTE_INPUT">
              <strong>{t("ACCESS_CARD.ORDER_MESSAGE.LABEL")}</strong>
            </label>
            <p style={{ marginBottom: 4 }}>
              {t("ACCESS_CARD.ORDER_MESSAGE.DESCRIPTION")}
            </p>
            <InputField
              id="ORDER_NOTE_INPUT"
              dark
              value={orderNote}
              maxLength={100}
              onChange={(e) => setOrderNote(e.target.value)}
              onClear={() => setOrderNote("")}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "end" }}>
            <Button
              disabled={isOrdering}
              isLoading={isOrdering}
              onClick={handleCreatePlasticCardOrder}
            >
              {t("ORDER")}
            </Button>
          </div>
        </div>
      </PopupCard>
    </>
  );
}
